import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { EavEntityTypeFindDto, type EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { DirectionFindDto, type DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';

import * as yup from 'yup';

/** HelpDesk task type response body */
export interface HelpdeskTaskTypeFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion date and time. The task type will be permanently deleted six months after this date. */
  deleted?: Date;
  /** The user who created this task type */
  createdBy?: UserFindInterface;
  /** Task type display name */
  name: string;
  /** Helpdesk task will inherit this priority */
  defaultPriority: HelpdeskTaskPriorityEnum;
  /** Helpdesk task will inherit direction's deadline */
  defaultDeadline: number;
  /** Related direction */
  direction: DirectionFindInterface;
  /** Related responsible user */
  responsible: UserFindInterface;
  /** Related user group auditors */
  userGroupAuditors: UserGroupFindInterface[];
  /** Related EAV entity type which contains additional fields for HD tasks with this task type */
  entityType?: EavEntityTypeFindInterface;
}

/** HelpDesk task type response body - DTO schema */
export const HelpdeskTaskTypeFindDto = SerdeDtoClass.create<HelpdeskTaskTypeFindInterface>(HelpdeskDtoKeyEnum.HELPDESK_TASK_TYPE_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  defaultPriority: {
    label: 'Приоритет заявки по-умолчанию',
    serializer: yup
      .string()
      .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
  },
  defaultDeadline: {
    label: 'Дедлайн по-умолчанию',
    serializer: yup.number(),
  },
  direction: {
    sortable: true,
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  responsible: {
    sortable: true,
    optional: true,
    label: 'Ответственный',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  userGroupAuditors: {
    sortable: true,
    optional: true,
    label: 'Группы аудиторов',
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  entityType: {
    optional: true,
    label: 'Шаблон полей',
    nested: EavEntityTypeFindDto,
    serializer: (_, nested) => nested,
  },
});
